import { SegmentInfoObj } from './segment-info-model'
import { MillisecondObj } from './millisecond-model'

/**
 * Model object to store information related to a Slice
 * @dependency {MillisecondObj}
 */
class SliceInfoObj {
  constructor(rawSlice) {
    this.rawSlice = rawSlice || {}

    this.indexID = undefined
    this.indexKey = undefined
    this.originAirportCode = undefined
    this.destinationAirportCode = undefined
    this.departureDateTime = []
    this.arrivalDateTime = []

    this.arrivingInDays = 0 // -1(previous-day), 0(same-day), 1(over-night), 2(2-days-later)
    this.stops = undefined
    this.duration = undefined
    this.connectionDuration = undefined
    this.cabinClass = undefined
    this.allowedEquipment = undefined

    this.alternateOriginAirport = false
    this.alternateDestinationAirport = false

    this.segmentArray = []
  }

  getRawSlice() {
    return this.rawSlice
  }

  getIndexID() {
    return this.indexID
  }

  setIndexID(indexID) {
    this.indexID = indexID
  }

  getIndexKey() {
    return this.indexKey
  }

  setIndexKey(indexKey) {
    this.indexKey = indexKey
  }

  getOriginAirport() {
    return this.originAirportCode
  }

  setOriginAirport(originAirportCode) {
    this.originAirportCode = originAirportCode
  }

  getDestinationAirport() {
    return this.destinationAirportCode
  }

  setDestinationAirport(destinationAirportCode) {
    this.destinationAirportCode = destinationAirportCode
  }

  getDepartureDateTime() {
    return this.departureDateTime
  }

  addDepartureDateTime(departureDateTime) {
    this.departureDateTime.push(departureDateTime)
  }

  getArrivalDateTime() {
    return this.arrivalDateTime
  }

  addArrivalDateTime(arrivalDateTime) {
    this.arrivalDateTime.push(arrivalDateTime)
  }

  getArrivingInDays() {
    return this.arrivingInDays
  }

  setArrivingInDays(arrivingInDays) {
    if (arrivingInDays) {
      this.arrivingInDays = arrivingInDays
    }
  }

  getStops() {
    return this.stops
  }

  setStops(stops) {
    this.stops = stops
  }

  getDuration() {
    return this.duration
  }

  setDuration(duration) {
    this.duration = new MillisecondObj(duration)
  }

  getConnectionDuration() {
    return this.connectionDuration
  }

  setConnectionDuration(duration) {
    this.connectionDuration = new MillisecondObj(duration)
  }

  getCabinClass() {
    return this.cabinClass
  }

  setCabinClass(cabinClass) {
    this.cabinClass = cabinClass
  }

  getAllowedEquipment() {
    return this.allowedEquipment
  }

  setAllowedEquipment(allowedEquipment) {
    this.allowedEquipment = allowedEquipment
  }

  isAlternateOriginAirport() {
    return this.alternateOriginAirport
  }

  setAlternateOriginAirport(alternateOriginAirport) {
    this.alternateOriginAirport = !!alternateOriginAirport
  }

  isAlternateDestinationAirport() {
    return this.alternateDestinationAirport
  }

  setAlternateDestinationAirport(alternateDestinationAirport) {
    this.alternateDestinationAirport = !!alternateDestinationAirport
  }

  update(rawSlice) {
    if (rawSlice) {
      this.rawSlice = rawSlice

      // Presence of segment means a retail slice
      if (rawSlice.segment) {
        this.setIndexID(rawSlice.id || rawSlice.uniqueSliceId)
        this.setIndexKey(rawSlice.sliceKey)

        if (rawSlice.overnightLayover) {
          this.setArrivingInDays(1)
        }

        if (rawSlice.segment) {
          let segmentObj

          for (let i = 0; i < rawSlice.segment.length; i++) {
            segmentObj = new SegmentInfoObj()
            segmentObj.update(rawSlice.segment[i])

            this.segmentArray.push(segmentObj)
            if (segmentObj.isOvernightFlight()) {
              this.setArrivingInDays(1)
            }
          }

          this.setOriginAirport(this.segmentArray[0].getOriginAirport())
          this.setDestinationAirport(
            this.segmentArray[
              this.segmentArray.length - 1
            ].getDestinationAirport()
          )
          this.addDepartureDateTime(this.segmentArray[0].getDepartureDate())
          this.addArrivalDateTime(
            this.segmentArray[this.segmentArray.length - 1].getArrivalDate()
          )
          // Setting any other data does not seem necessary as of now
        }

        this.setDuration(rawSlice.duration)
      } else {
        this.setIndexID(rawSlice.id)
        this.setIndexKey(rawSlice.sliceKey)

        if (rawSlice.mayArriveNextDay) {
          this.setArrivingInDays(1)
        } else if (rawSlice.mayArrivePreviousDay) {
          this.setArrivingInDays(-1)
        }

        this.setOriginAirport(this.rawSlice.origin.location)
        this.setDestinationAirport(this.rawSlice.destination.location)
        this.setAlternateOriginAirport(this.rawSlice.origin.alternate)
        this.setAlternateDestinationAirport(this.rawSlice.destination.alternate)
        this.addDepartureDateTime(this.rawSlice.departureWindow.start)
        this.addDepartureDateTime(this.rawSlice.departureWindow.end)
        this.addArrivalDateTime(this.rawSlice.arrivalWindow.start)
        this.addArrivalDateTime(this.rawSlice.arrivalWindow.end)

        this.setDuration(rawSlice.maximumDuration)
        this.setConnectionDuration(rawSlice.maximumConnectionDuration)
        this.setStops(rawSlice.maximumStops)
        this.setCabinClass(rawSlice.cabinClass)
        this.setAllowedEquipment(rawSlice.allowedEquipment)
      }
    }
  }
}

export { SliceInfoObj }
